import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapPin, Truck, Package, CheckCircle, Clock, X, Navigation, Box, CircleDot } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FTL_STAGES = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#22c55e' },
  { key: 'vehicle_assigned', label: 'Vehicle Assigned', icon: Truck, color: '#8b5cf6' },
  { key: 'vehicle_arrived', label: 'Vehicle Arrived', icon: MapPin, color: '#3b82f6' },
  { key: 'in_transit', label: 'In Transit', icon: Truck, color: '#f59e0b' },
  { key: 'reached_delivery', label: 'Reached Delivery', icon: MapPin, color: '#06b6d4' },
  { key: 'delivered', label: 'Delivered', icon: Package, color: '#10b981' },
];

const PTL_STAGES = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#22c55e' },
  { key: 'dispatched', label: 'Dispatched', icon: Box, color: '#8b5cf6' },
  { key: 'in_transit', label: 'In Transit', icon: Truck, color: '#f59e0b' },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: CircleDot, color: '#06b6d4' },
  { key: 'delivered', label: 'Delivered', icon: Package, color: '#10b981' },
];

// 3D Canvas Animation
const Tracking3DCanvas = ({ status, stages, transportType }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  const stageIndex = stages.findIndex(s => s.key === status);
  const progress = stageIndex >= 0 ? stageIndex / (stages.length - 1) : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.offsetWidth * 2;
    const H = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const w = W / 2, h = H / 2;
    let frame = 0;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      frame++;

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, w, h);
      bg.addColorStop(0, '#0f172a');
      bg.addColorStop(1, '#1e293b');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Animated grid
      ctx.strokeStyle = 'rgba(148,163,184,0.06)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < w; i += 30) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, h);
        ctx.stroke();
      }
      for (let i = 0; i < h; i += 30) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(w, i);
        ctx.stroke();
      }

      // Route path
      const pathY = h * 0.5;
      const startX = 40;
      const endX = w - 40;

      // Dotted line (full route)
      ctx.setLineDash([4, 6]);
      ctx.strokeStyle = 'rgba(148,163,184,0.2)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startX, pathY);
      ctx.lineTo(endX, pathY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Completed route (glowing)
      const progressX = startX + (endX - startX) * progress;
      const routeGrad = ctx.createLinearGradient(startX, 0, progressX, 0);
      routeGrad.addColorStop(0, '#22c55e');
      routeGrad.addColorStop(1, '#f59e0b');
      ctx.strokeStyle = routeGrad;
      ctx.lineWidth = 3;
      ctx.shadowColor = '#f59e0b';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(startX, pathY);
      ctx.lineTo(progressX, pathY);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Stage dots
      stages.forEach((stage, i) => {
        const x = startX + (endX - startX) * (i / (stages.length - 1));
        const active = i <= stageIndex;
        const current = i === stageIndex;

        // Pulsing ring for current
        if (current) {
          const pulse = Math.sin(frame * 0.05) * 4 + 12;
          ctx.beginPath();
          ctx.arc(x, pathY, pulse, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(245,158,11,${0.15 + Math.sin(frame * 0.05) * 0.1})`;
          ctx.fill();
        }

        // Dot
        ctx.beginPath();
        ctx.arc(x, pathY, active ? 8 : 5, 0, Math.PI * 2);
        ctx.fillStyle = active ? stage.color : '#475569';
        ctx.fill();
        if (active) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        // Label
        ctx.fillStyle = active ? '#f8fafc' : '#64748b';
        ctx.font = `${active ? 'bold ' : ''}${w > 300 ? 9 : 7}px system-ui`;
        ctx.textAlign = 'center';
        const label = stage.label.length > 12 ? stage.label.substring(0, 11) + '..' : stage.label;
        ctx.fillText(label, x, pathY + 22);
      });

      // Truck icon at progress point
      if (status !== 'delivered' && stageIndex >= 0) {
        const truckX = progressX;
        const bounce = Math.sin(frame * 0.08) * 3;
        ctx.save();
        ctx.translate(truckX, pathY - 25 + bounce);

        // Truck body
        ctx.fillStyle = '#f59e0b';
        ctx.shadowColor = '#f59e0b';
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.roundRect(-14, -8, 20, 14, 3);
        ctx.fill();
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath();
        ctx.roundRect(6, -4, 10, 10, [0, 3, 3, 0]);
        ctx.fill();
        // Wheels
        ctx.fillStyle = '#1e293b';
        ctx.beginPath();
        ctx.arc(-6, 8, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(10, 8, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.restore();
      }

      // Delivered checkmark
      if (status === 'delivered') {
        const cx = endX, cy = pathY - 25;
        const scale = Math.min(1, (frame % 120) / 30);
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(scale, scale);
        ctx.fillStyle = '#10b981';
        ctx.shadowColor = '#10b981';
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(0, 0, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(-6, 0);
        ctx.lineTo(-2, 5);
        ctx.lineTo(7, -4);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.restore();
      }

      // Floating particles
      for (let i = 0; i < 8; i++) {
        const px = (frame * 0.3 + i * 47) % w;
        const py = h * 0.2 + Math.sin(frame * 0.02 + i) * 20;
        ctx.fillStyle = `rgba(245,158,11,${0.05 + Math.sin(frame * 0.03 + i) * 0.03})`;
        ctx.beginPath();
        ctx.arc(px, py, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [status, stages, stageIndex, progress]);

  return <canvas ref={canvasRef} className="w-full rounded-xl" style={{ height: '160px' }} />;
};

const TrackingTimeline = ({ updates }) => {
  if (!updates || updates.length === 0) return null;
  return (
    <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="tracking-timeline-list">
      {[...updates].reverse().map((u, i) => (
        <div key={u.updated_at || i} className="flex gap-3 text-sm">
          <div className="flex flex-col items-center">
            <div className={`w-3 h-3 rounded-full ${i === 0 ? 'bg-amber-500 ring-2 ring-amber-200' : 'bg-slate-300 dark:bg-slate-600'}`} />
            {i < updates.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700" />}
          </div>
          <div className="pb-3 flex-1">
            <div className="flex items-center justify-between">
              <p className="font-bold text-slate-900 dark:text-white text-xs">{u.location}</p>
              <span className="text-[10px] text-slate-400">{u.event_date} {u.event_time}</span>
            </div>
            <p className="text-xs text-amber-600 font-semibold">{(u.status || '').replace(/_/g, ' ').toUpperCase()}</p>
            {u.notes && <p className="text-xs text-slate-500">{u.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

export const ShipmentTrackingMap = ({ trackingId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!trackingId) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/requests/${trackingId}/map-data`)
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [trackingId]);

  if (loading) return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-8" onClick={e => e.stopPropagation()}>
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto" />
        <p className="text-center mt-3 text-slate-500 text-sm">Loading...</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-8" onClick={e => e.stopPropagation()}>
        <p className="text-red-500">Tracking data unavailable</p>
        <button onClick={onClose} className="mt-3 w-full py-2 bg-slate-200 dark:bg-slate-700 rounded-lg text-sm">Close</button>
      </div>
    </div>
  );

  const isFTL = data.transport_type === 'FTL';
  const stages = isFTL ? FTL_STAGES : PTL_STAGES;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="tracking-map-modal">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Navigation className="w-4 h-4 text-amber-500" /> Shipment Tracking
            </h2>
            <p className="text-xs text-slate-500">{data.tracking_id} | {isFTL ? 'Full Truck Load' : 'Part Load (Courier)'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="close-tracking-map"><X className="w-5 h-5" /></button>
        </div>

        {/* 3D Canvas */}
        <div className="px-4 pt-3">
          <Tracking3DCanvas status={data.status} stages={stages} transportType={data.transport_type} />
        </div>

        {/* Route Info */}
        <div className="px-4 py-3 grid grid-cols-2 gap-3">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-[10px] font-bold text-green-700 dark:text-green-400 flex items-center gap-1"><MapPin className="w-3 h-3" />PICKUP</p>
            <p className="font-bold text-sm text-slate-900 dark:text-white">{data.pickup?.city || data.pickup?.pincode}</p>
            <p className="text-[10px] text-slate-500">{data.pickup?.state} - {data.pickup?.pincode}</p>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-[10px] font-bold text-red-700 dark:text-red-400 flex items-center gap-1"><MapPin className="w-3 h-3" />DELIVERY</p>
            <p className="font-bold text-sm text-slate-900 dark:text-white">{data.delivery?.city || data.delivery?.pincode}</p>
            <p className="text-[10px] text-slate-500">{data.delivery?.state} - {data.delivery?.pincode}</p>
          </div>
        </div>

        {/* LRN / Courier Reference */}
        {data.lrn_number && (
          <div className="mx-4 mb-2 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <p className="text-[10px] font-bold text-indigo-700 dark:text-indigo-400">LRN / COURIER REF</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white font-mono">{data.lrn_number}</p>
          </div>
        )}

        {/* Vehicle Info (FTL only) */}
        {isFTL && data.vehicle?.number && (
          <div className="mx-4 mb-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1"><Truck className="w-3 h-3" />VEHICLE</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white">{data.vehicle.number}</p>
            {data.vehicle.driver_name && <p className="text-xs text-slate-500">Driver: {data.vehicle.driver_name} | {data.vehicle.driver_phone}</p>}
          </div>
        )}

        {/* Current Location */}
        {data.current_location && (
          <div className="mx-4 mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
            <p className="text-xs text-blue-600 font-semibold">Current Location: {data.current_location}</p>
          </div>
        )}

        {/* Timeline */}
        {data.updates?.length > 0 && (
          <div className="mx-4 mb-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-3">TRACKING HISTORY</h4>
            <TrackingTimeline updates={data.updates} />
          </div>
        )}
      </div>
    </div>
  );
};

// Public tracking (for homepage)
export const PublicTrackingResult = ({ data, onClose }) => {
  if (!data) return null;
  const isFTL = data.transport_type === 'FTL';
  const stages = isFTL ? FTL_STAGES : PTL_STAGES;

  return (
    <div className="mt-6 bg-white/10 dark:bg-black/40 backdrop-blur-2xl border border-white/20 rounded-2xl p-6" data-testid="public-tracking-result">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white">{data.tracking_id}</h3>
          <p className="text-xs text-white/60">{isFTL ? 'Full Truck Load' : 'Part Load'} | {(data.status || '').replace(/_/g, ' ').toUpperCase()}</p>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <Tracking3DCanvas status={data.status} stages={stages} transportType={data.transport_type} />
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="p-2 bg-white/5 rounded-lg"><p className="text-[10px] text-green-400 font-bold">PICKUP</p><p className="text-sm font-bold text-white">{data.pickup_city || data.pickup_pincode}</p></div>
        <div className="p-2 bg-white/5 rounded-lg"><p className="text-[10px] text-red-400 font-bold">DELIVERY</p><p className="text-sm font-bold text-white">{data.delivery_city || data.delivery_pincode}</p></div>
      </div>
      {data.lrn_number && <p className="text-center text-xs text-indigo-400 mt-2 font-mono">LRN: {data.lrn_number}</p>}
      {data.current_location && <p className="text-center text-xs text-amber-400 mt-1">Current: {data.current_location}</p>}
      {data.updates?.length > 0 && (
        <div className="mt-3 p-3 bg-white/5 rounded-lg max-h-40 overflow-y-auto">
          {[...data.updates].reverse().map((u, i) => (
            <div key={u.updated_at || `upd-${i}`} className="flex justify-between text-xs text-white/70 py-1 border-b border-white/5 last:border-0">
              <span className="font-semibold">{u.location} — <span className="text-amber-400">{(u.status||'').replace(/_/g,' ')}</span></span>
              <span className="text-white/40">{u.event_date} {u.event_time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
